import type { ForecastOverview } from '@ft/shared';
import { Link } from 'react-router-dom';
import type { T } from '../i18n';
import { keur, skeur } from '../lib/format';

/**
 * Erklärt Forecast und Erwartung in einem Satz und einer Brücke:
 * Erwartung = Ist bis Vormonat + Forecast ab laufendem Monat. Der Unterschied zum Forecast ist der Teil,
 * der bis zum Vormonat geplant, aber nicht abgerechnet ist.
 */
export function ForecastExplain({ data, t }: { data: ForecastOverview; t: T }) {
  const tot = data.totals;
  const cm = data.currentMonth;
  if (cm <= 0 || cm > 11 || !tot.forecastTotalCents) return null;
  const months = t('pp.months').split(',');
  const planned = tot.forecastTotalCents - tot.forecastRestCents;
  const gap = tot.forecastTotalCents - tot.expectedTotalCents;
  const actual = tot.expectedTotalCents - tot.forecastRestCents;
  const range = `${months[0]}–${months[cm - 1]}`;
  const restRange = `${months[cm]}–${months[11]}`;
  const target = tot.targetCents;
  const lead = gap > 100_000
    ? t('fc.say.gap', { fc: `<b>${keur(tot.forecastTotalCents)}</b>`, range, planned: `<b>${keur(planned)}</b>`, actual: `<b>${keur(actual)}</b>`, gap: `<b>${keur(gap)}</b>`,
      dFc: target ? skeur(tot.forecastTotalCents - target) : '–', exp: `<b>${keur(tot.expectedTotalCents)}</b>`, dExp: target ? skeur(tot.expectedTotalCents - target) : '–' })
    : t('fc.say.ok', { exp: `<b>${keur(tot.expectedTotalCents)}</b>`, dExp: target ? skeur(tot.expectedTotalCents - target) : '–' });
  const bars: { label: string; v: number; base: number; cls: string }[] = [
    { label: t('fc.br.forecast'), v: tot.forecastTotalCents, base: 0, cls: 'b-fc' },
    { label: t('fc.br.rest', { r: restRange }), v: tot.forecastRestCents, base: planned, cls: 'b-fc2' },
    { label: t('fc.br.planned', { r: range }), v: planned, base: 0, cls: 'b-fc' },
    { label: t('fc.br.gap'), v: Math.max(0, gap), base: actual, cls: 'b-gap' },
    { label: t('fc.br.actual'), v: actual, base: 0, cls: 'b-act' },
    { label: t('fc.br.expected', { r: restRange }), v: tot.expectedTotalCents, base: 0, cls: 'b-exp' },
  ];
  const max = Math.max(tot.forecastTotalCents, tot.expectedTotalCents, target) * 1.05 || 1;
  const byProject = data.projects.map((p) => ({ p, gap: p.forecastTotalCents - p.expectedTotalCents })).filter((x) => x.gap > 100_000).sort((a, b) => b.gap - a.gap).slice(0, 8);
  const mx = byProject[0]?.gap ?? 1;
  return (
    <>
      <p className="say" dangerouslySetInnerHTML={{ __html: lead }} />
      {gap > 100_000 && (
        <div className="ppgrid">
          <div className="box">
            <div className="panel-h"><h2>{t('fc.br.title')}</h2><span className="hint">{target ? t('fc.br.target', { t: keur(target) }) : ''}</span></div>
            <div className="bridge" role="img" aria-label={t('fc.br.title')}>
              {bars.map((b) => (
                <div className="bcol" key={b.label}>
                  <div className="bval num">{keur(b.v)}</div>
                  <div className={`bblk ${b.cls}`} style={{ height: `${(b.v / max) * 170}px`, marginBottom: `${(b.base / max) * 170}px` }} />
                  <div className="bcap">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="box">
            <div className="panel-h"><h2>{t('fc.br.where', { gap: keur(gap) })}</h2></div>
            <table className="gaptable"><tbody>
              {byProject.map(({ p, gap: g }) => (
                <tr key={p.id}><td><Link to={`/projekte/${p.id}`}>{p.name}</Link> <span className="muted small">{p.customer.name}</span></td>
                  <td style={{ width: '38%' }}><div className="meter"><i style={{ width: `${(g / mx) * 100}%`, background: 'var(--crit)' }} /></div></td>
                  <td className="r num">{keur(g)}</td></tr>
              ))}
            </tbody></table>
            <p className="muted small" style={{ margin: '8px 18px 12px' }}>{t('fc.br.whereHint', { r: range })}</p>
          </div>
        </div>
      )}
    </>
  );
}
